import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { usePacientes } from '../hooks/usePacientes';
import {
  createPreConsultationForm,
  getPreConsultationForm,
  getPendingPreConsultationForms,
  savePreConsultationForm,
  submitPreConsultationForm,
  markPreConsultationAsReviewed,
  PreConsultationForm,
} from '../services/preConsultationService';
import { PreConsultationFormComponent } from '../components/preConsultation/PreConsultationFormComponent';
import './Pages.css';

export const PreConsultation: React.FC = () => {
  const { user } = useAuth();
  const { pacientes } = usePacientes();
  const [searchParams, setSearchParams] = useSearchParams();
  const formId = searchParams.get('formId');
  const patientId = searchParams.get('patientId');
  const consultationId = searchParams.get('consultationId');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentForm, setCurrentForm] = useState<PreConsultationForm | null>(null);
  const [pendingForms, setPendingForms] = useState<PreConsultationForm[]>([]);
  const [viewMode, setViewMode] = useState<'create' | 'edit' | 'review' | 'list'>('list');
  const [selectedPatientId, setSelectedPatientId] = useState<string>(patientId || '');

  // Determinar modo de visualização baseado no role do usuário
  const userRole = (user as any)?.role || 'patient';
  const isNutritionist = userRole === 'nutritionist';

  useEffect(() => {
    if (isNutritionist) {
      loadPendingForms();
    } else if (formId) {
      loadForm(formId);
    } else if (consultationId) {
      // Criar novo formulário para consulta
      handleCreateForm(consultationId);
    }
  }, [user, formId, consultationId, isNutritionist]);

  const loadForm = async (id: string) => {
    setLoading(true);
    try {
      const form = await getPreConsultationForm(id);
      if (form) {
        setCurrentForm(form);
        setViewMode(form.status === 'pending' && !isNutritionist ? 'edit' : 'review');
        setSelectedPatientId(form.patientId);
      }
    } catch (error) {
      console.error('Erro ao carregar formulário:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingForms = async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const forms = await getPendingPreConsultationForms(user.uid);
      setPendingForms(forms);
    } catch (error) {
      console.error('Erro ao carregar formulários pendentes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = async (consultationIdParam?: string) => {
    if (!user?.uid) return;
    
    setSaving(true);
    try {
      const patientIdToUse = selectedPatientId || user.uid;
      const nutritionistId = isNutritionist ? user.uid : (user as any).nutritionistId || user.uid;
      
      const newFormId = await createPreConsultationForm(
        nutritionistId,
        patientIdToUse,
        consultationIdParam || consultationId || undefined
      );
      
      const form = await getPreConsultationForm(newFormId);
      if (form) {
        setCurrentForm(form);
        setViewMode('edit');
        setSearchParams({ formId: newFormId });
      }
    } catch (error) {
      console.error('Erro ao criar formulário:', error);
      alert('Erro ao criar formulário pré-consulta');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveForm = async (formData: Partial<PreConsultationForm>) => {
    if (!currentForm?.id) return;
    
    setSaving(true);
    try {
      await savePreConsultationForm(currentForm.id, formData);
      const updatedForm = await getPreConsultationForm(currentForm.id);
      if (updatedForm) {
        setCurrentForm(updatedForm);
      }
      alert('Formulário salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar formulário:', error);
      alert('Erro ao salvar formulário');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForm = async () => {
    if (!currentForm?.id) return;
    
    if (!window.confirm('Tem certeza que deseja submeter este formulário? Após a submissão, você não poderá mais editá-lo.')) {
      return;
    }
    
    setSaving(true);
    try {
      await submitPreConsultationForm(currentForm.id);
      const updatedForm = await getPreConsultationForm(currentForm.id);
      if (updatedForm) {
        setCurrentForm(updatedForm);
        setViewMode('review');
      }
      alert('Formulário submetido com sucesso! O nutricionista será notificado.');
    } catch (error) {
      console.error('Erro ao submeter formulário:', error);
      alert('Erro ao submeter formulário');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsReviewed = async (formId: string) => {
    setSaving(true);
    try {
      await markPreConsultationAsReviewed(formId);
      await loadPendingForms();
      alert('Formulário marcado como revisado!');
    } catch (error) {
      console.error('Erro ao marcar como revisado:', error);
      alert('Erro ao marcar formulário');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="ns-page">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  // Modo lista (nutricionista vendo formulários pendentes)
  if (isNutritionist && viewMode === 'list' && !formId) {
    return (
      <div className="ns-page">
        <div className="ns-page-header">
          <div>
            <h2>Pré-Consultas</h2>
            <p>Revise os formulários preenchidos pelos pacientes antes das consultas</p>
          </div>
          <Button onClick={() => setViewMode('create')}>+ Criar Formulário</Button>
        </div>

        {pendingForms.length === 0 ? (
          <Card title="Nenhum formulário pendente" subtitle="Não há formulários aguardando revisão">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: 'var(--page-text-muted)', fontSize: '16px', marginBottom: '16px' }}>
                Quando pacientes preencherem formulários pré-consulta, eles aparecerão aqui.
              </p>
              <Button onClick={() => setViewMode('create')}>Criar Primeiro Formulário</Button>
            </div>
          </Card>
        ) : (
          <div className="ns-grid ns-grid-2" style={{ gap: '20px' }}>
            {pendingForms.map((form) => {
              const patient = pacientes.find(p => p.id === form.patientId);
              const personalInfo = form.personalInfo || {};
              const healthInfo = form.healthInfo || {};
              const goals = form.goals || {};
              
              return (
                <Card
                  key={form.id}
                  title={patient?.nome || 'Paciente'}
                  subtitle={`Submetido em ${new Date(form.submittedAt).toLocaleDateString('pt-BR', { 
                    day: '2-digit', 
                    month: 'long', 
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}`}
                  actions={
                    <span style={{ 
                      fontSize: '12px', 
                      fontWeight: 'normal',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: form.status === 'pending' ? '#FEF3C7' : '#D1FAE5',
                      color: form.status === 'pending' ? '#92400E' : '#065F46'
                    }}>
                      {form.status === 'pending' ? '⏳ Pendente' : '✅ Revisado'}
                    </span>
                  }
                >
                  {/* Resumo rápido das informações principais */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)', 
                    gap: '12px',
                    marginBottom: '20px',
                    padding: '16px',
                    backgroundColor: '#F8FAFC',
                    borderRadius: '8px'
                  }}>
                    {personalInfo.age && (
                      <div>
                        <span style={{ fontSize: '12px', color: 'var(--page-text-muted)', display: 'block' }}>Idade</span>
                        <span style={{ fontSize: '16px', fontWeight: '600' }}>{personalInfo.age} anos</span>
                      </div>
                    )}
                    {personalInfo.height && personalInfo.weight && (
                      <div>
                        <span style={{ fontSize: '12px', color: 'var(--page-text-muted)', display: 'block' }}>IMC</span>
                        <span style={{ fontSize: '16px', fontWeight: '600' }}>
                          {((personalInfo.weight / ((personalInfo.height / 100) ** 2))).toFixed(1)}
                        </span>
                      </div>
                    )}
                    {personalInfo.gender && (
                      <div>
                        <span style={{ fontSize: '12px', color: 'var(--page-text-muted)', display: 'block' }}>Gênero</span>
                        <span style={{ fontSize: '16px', fontWeight: '600' }}>
                          {personalInfo.gender === 'male' ? 'Masculino' : 
                           personalInfo.gender === 'female' ? 'Feminino' : 'Outro'}
                        </span>
                      </div>
                    )}
                    {goals.primaryGoal && (
                      <div>
                        <span style={{ fontSize: '12px', color: 'var(--page-text-muted)', display: 'block' }}>Objetivo</span>
                        <span style={{ fontSize: '16px', fontWeight: '600' }}>
                          {goals.primaryGoal === 'weight_loss' ? 'Perda de Peso' :
                           goals.primaryGoal === 'weight_gain' ? 'Ganho de Peso' :
                           goals.primaryGoal === 'muscle_gain' ? 'Ganho de Massa' :
                           goals.primaryGoal === 'maintenance' ? 'Manutenção' :
                           goals.primaryGoal === 'health' ? 'Melhorar Saúde' :
                           goals.primaryGoal === 'performance' ? 'Performance' : goals.primaryGoal}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Alertas importantes */}
                  {((healthInfo.medicalConditions && healthInfo.medicalConditions.length > 0) || 
                    (healthInfo.allergies && healthInfo.allergies.length > 0)) && (
                    <div style={{ 
                      marginBottom: '16px',
                      padding: '12px',
                      backgroundColor: '#FEE2E2',
                      borderRadius: '6px',
                      borderLeft: '4px solid #EF4444'
                    }}>
                      <strong style={{ fontSize: '13px', color: '#991B1B', display: 'block', marginBottom: '8px' }}>
                        ⚠️ Atenção
                      </strong>
                      {healthInfo.medicalConditions && healthInfo.medicalConditions.length > 0 && (
                        <p style={{ fontSize: '12px', color: '#7F1D1D', margin: '4px 0' }}>
                          <strong>Condições:</strong> {healthInfo.medicalConditions.join(', ')}
                        </p>
                      )}
                      {healthInfo.allergies && healthInfo.allergies.length > 0 && (
                        <p style={{ fontSize: '12px', color: '#7F1D1D', margin: '4px 0' }}>
                          <strong>Alergias:</strong> {healthInfo.allergies.join(', ')}
                        </p>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchParams({ formId: form.id! });
                        loadForm(form.id!);
                      }}
                      style={{ flex: 1 }}
                    >
                      📋 Ver Completo
                    </Button>
                    {form.status === 'pending' && (
                      <Button
                        onClick={() => handleMarkAsReviewed(form.id!)}
                        disabled={saving}
                        style={{ flex: 1 }}
                      >
                        ✅ Marcar Revisado
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Modo criar (nutricionista criando formulário para paciente)
  if (isNutritionist && viewMode === 'create') {
    return (
      <div className="ns-page">
        <div className="ns-page-header">
          <div>
            <h2>Criar Formulário Pré-Consulta</h2>
            <p>Selecione um paciente para criar um formulário pré-consulta.</p>
          </div>
          <Button variant="outline" onClick={() => setViewMode('list')}>
            Voltar
          </Button>
        </div>

        <Card title="Seleção de Paciente" subtitle="Escolha o paciente">
          <div className="ns-form">
            <label className="ns-field">
              <span>Paciente *</span>
              <select
                className="ns-input"
                value={selectedPatientId}
                onChange={(e) => setSelectedPatientId(e.target.value)}
              >
                <option value="">Selecione um paciente</option>
                {pacientes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </select>
            </label>
            <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
              <Button
                onClick={() => handleCreateForm()}
                disabled={!selectedPatientId || saving}
              >
                Criar Formulário
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Modo edição/revisão (formulário específico)
  if (currentForm) {
    const patient = pacientes.find(p => p.id === currentForm.patientId);
    
    return (
      <div className="ns-page">
        <div className="ns-page-header">
          <div>
            <h2>
              {isNutritionist ? (
                <>Pré-Consulta: <span style={{ color: 'var(--page-text-muted)', fontWeight: 'normal' }}>{patient?.nome || 'Paciente'}</span></>
              ) : (
                'Formulário Pré-Consulta'
              )}
            </h2>
            <p>
              {isNutritionist 
                ? 'Revise as informações preenchidas pelo paciente antes da consulta'
                : viewMode === 'edit' 
                  ? 'Preencha o formulário antes da sua consulta' 
                  : 'Visualizando formulário pré-consulta'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {isNutritionist && currentForm.status === 'pending' && (
              <Button
                onClick={() => handleMarkAsReviewed(currentForm.id!)}
                disabled={saving}
              >
                ✅ Marcar como Revisado
              </Button>
            )}
            {isNutritionist && (
              <Button variant="outline" onClick={() => {
                setViewMode('list');
                setSearchParams({});
                setCurrentForm(null);
              }}>
                ← Voltar para Lista
              </Button>
            )}
          </div>
        </div>

        {/* Informações do paciente destacadas para nutricionista */}
        {isNutritionist && patient && (
          <Card 
            title="Informações do Paciente" 
            subtitle={patient.email || 'Sem email cadastrado'}
            style={{ marginBottom: '20px' }}
          >
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px' 
            }}>
              <div>
                <span style={{ fontSize: '12px', color: 'var(--page-text-muted)', display: 'block' }}>Nome Completo</span>
                <span style={{ fontSize: '16px', fontWeight: '600' }}>{patient.nome}</span>
              </div>
              {patient.email && (
                <div>
                  <span style={{ fontSize: '12px', color: 'var(--page-text-muted)', display: 'block' }}>Email</span>
                  <span style={{ fontSize: '16px', fontWeight: '600' }}>{patient.email}</span>
                </div>
              )}
              {currentForm.personalInfo?.phone && (
                <div>
                  <span style={{ fontSize: '12px', color: 'var(--page-text-muted)', display: 'block' }}>Telefone</span>
                  <span style={{ fontSize: '16px', fontWeight: '600' }}>{currentForm.personalInfo.phone}</span>
                </div>
              )}
              {currentForm.personalInfo?.age && (
                <div>
                  <span style={{ fontSize: '12px', color: 'var(--page-text-muted)', display: 'block' }}>Idade</span>
                  <span style={{ fontSize: '16px', fontWeight: '600' }}>{currentForm.personalInfo.age} anos</span>
                </div>
              )}
              {currentForm.personalInfo?.height && currentForm.personalInfo?.weight && (
                <div>
                  <span style={{ fontSize: '12px', color: 'var(--page-text-muted)', display: 'block' }}>IMC</span>
                  <span style={{ fontSize: '16px', fontWeight: '600' }}>
                    {((currentForm.personalInfo.weight / ((currentForm.personalInfo.height / 100) ** 2))).toFixed(1)}
                  </span>
                </div>
              )}
            </div>
          </Card>
        )}

        <PreConsultationFormComponent
          form={currentForm}
          onSave={handleSaveForm}
          onSubmit={viewMode === 'edit' ? handleSubmitForm : undefined}
          readOnly={viewMode === 'review' || currentForm.status === 'reviewed' || isNutritionist}
          isNutritionist={isNutritionist}
        />
      </div>
    );
  }

  // Modo padrão (paciente sem formulário)
  return (
    <div className="ns-page">
      <div className="ns-page-header">
        <div>
          <h2>Formulário Pré-Consulta</h2>
          <p>Preencha o formulário antes da sua consulta para otimizar o tempo com o nutricionista.</p>
        </div>
      </div>

      <Card title="Nenhum formulário disponível" subtitle="Aguarde o nutricionista criar um formulário para você">
        <p style={{ color: 'var(--page-text-muted)', fontSize: '14px' }}>
          Quando o nutricionista criar um formulário pré-consulta para você, ele aparecerá aqui.
        </p>
      </Card>
    </div>
  );
};

